import React, { memo, useCallback, useMemo, useState } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Tooltip, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import { gridLines } from "./forecastTree";
import ForecastGrid, { fmtMoney, fmtUnits } from "./ForecastGrid";
import SkuDetail from "./SkuDetail";

/*
 * Product and SKU cards of the View Forecast page. Both are memoised and each
 * product keeps its own SKUs' open state, so opening or closing one card only
 * re-renders that card — not every grid on the page.
 */

// Below plan: amber (red is kept for problems); above: green.
const gapColor = (v, textSec, darkMode) =>
  v == null ? textSec : v < 0 ? (darkMode ? "#f0a55a" : "#b45309") : v > 0 ? (darkMode ? "#6fcf8f" : "#2e7d32") : textSec;

/** Small "Plan · Outlook · Gap" summary for a card header. */
export const NodeSummary = memo(function NodeSummary({ node, closed, textSec, darkMode }) {
  const { u, m } = useMemo(
    () => ({ u: gridLines(node, closed, "_units").year, m: gridLines(node, closed, "").year }),
    [node, closed],
  );
  const item = (label, value, color) => (
    <Box sx={{ textAlign: "left", minWidth: 92 }}>
      <Typography sx={{ fontSize: 9.5, color: textSec, textTransform: "uppercase" }}>{label}</Typography>
      <Typography sx={{ fontSize: 12.5, fontWeight: 600, color }}>{value}</Typography>
    </Box>
  );
  return (
    <Box sx={{ display: "flex", gap: 1.5, ml: "auto", mr: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
      {item("Plan units", fmtUnits(u.plan))}
      {item("Outlook units", fmtUnits(u.outlook))}
      {item("Gap units", u.delta == null ? "–" : `${u.delta > 0 ? "+" : ""}${fmtUnits(u.delta)}`, gapColor(u.delta, textSec, darkMode))}
      {item("Gap $", m.delta == null ? "–" : `${m.delta > 0 ? "+" : ""}${fmtMoney(m.delta)}`, gapColor(m.delta, textSec, darkMode))}
    </Box>
  );
});

const SkuCard = memo(function SkuCard({ sku, closed, darkMode, textSec, open, onToggle }) {
  return (
    <Accordion
      disableGutters
      expanded={open}
      onChange={(_, isOpen) => onToggle(sku.sku, isOpen)}
      TransitionProps={{ unmountOnExit: true }}
      sx={{ mb: 0.5, boxShadow: "none", border: `1px solid ${darkMode ? "#333" : "#e0e0e0"}` }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: "flex", alignItems: "center", width: "100%", gap: 1, flexWrap: "wrap" }}>
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{sku.sku}</Typography>
            <Typography sx={{ fontSize: 11, color: textSec }}>
              {sku.share != null ? `${Math.round(sku.share * 1000) / 10}% of category plan` : "no plan share"}
              {sku.share_filled_months > 0 && (
                <Tooltip
                  title={`New SKU: its share includes ${sku.share_filled_months} estimated month(s) before its first sale, filled from ${
                    sku.share_fill_source === "category" ? "the category" : sku.share_fill_source === "product" ? "its sibling SKUs" : "its sibling SKUs and the category"
                  } (sales fill).`}
                >
                  <span style={{ color: "#ed6c02" }}>{` · incl. ${sku.share_filled_months} est. months`}</span>
                </Tooltip>
              )}
              {sku.list_price ? ` · $${sku.list_price}` : " · no list price"}
            </Typography>
          </Box>
          <NodeSummary node={sku} closed={closed} textSec={textSec} darkMode={darkMode} />
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <ForecastGrid node={sku} closed={closed} darkMode={darkMode} />
        <SkuDetail sku={sku} closed={closed} darkMode={darkMode} />
      </AccordionDetails>
    </Accordion>
  );
});

/**
 * One product: its grid, and its SKUs (each a card). "Expand" opens the
 * product and every SKU in it; "Download" saves this product's numbers as
 * Excel, one block per SKU.
 */
export const ProductCard = memo(function ProductCard({
  product,
  closed,
  darkMode,
  textSec,
  open,
  onToggle, // (productId, open) => void
  onDownload, // (product) => void
}) {
  const [openSku, setOpenSku] = useState({});
  const allSkusOpen = product.skus.length > 0 && product.skus.every((s) => openSku[s.sku]);

  const toggleSku = useCallback((sku, isOpen) => setOpenSku((prev) => ({ ...prev, [sku]: isOpen })), []);
  const toggleAllSkus = (e) => {
    e.stopPropagation(); // don't also open/close the card
    const next = !allSkusOpen;
    setOpenSku(next ? Object.fromEntries(product.skus.map((s) => [s.sku, true])) : {});
    if (next && !open) onToggle(product.id, true);
  };
  const download = (e) => {
    e.stopPropagation();
    onDownload(product);
  };

  return (
    <Accordion
      disableGutters
      expanded={open}
      onChange={(_, isOpen) => onToggle(product.id, isOpen)}
      TransitionProps={{ unmountOnExit: true }}
      sx={{ mb: 0.75 }}
    >
      {/* No chevron: the Expand / Collapse button is the one expand control
          (clicking the header still opens the product on its own). */}
      <AccordionSummary>
        <Box sx={{ display: "flex", alignItems: "center", width: "100%", gap: 1, flexWrap: "wrap" }}>
          <Box>
            <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{product.name}</Typography>
            <Typography sx={{ fontSize: 11, color: textSec }}>{product.skus.length} SKU(s)</Typography>
          </Box>
          <NodeSummary node={product} closed={closed} textSec={textSec} darkMode={darkMode} />
          <Box sx={{ display: "flex", gap: 0.5 }} onFocus={(e) => e.stopPropagation()}>
            <Tooltip title={allSkusOpen ? "Collapse this product's SKUs" : "Expand all SKUs of this product"}>
              <Button
                size="small"
                variant="text"
                startIcon={allSkusOpen ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
                onClick={toggleAllSkus}
                sx={{ textTransform: "none", fontSize: 12 }}
              >
                {allSkusOpen ? "Collapse" : "Expand"}
              </Button>
            </Tooltip>
            <Tooltip title="Download this product (Excel, one block per SKU)">
              <Button
                size="small"
                variant="text"
                startIcon={<FileDownloadOutlinedIcon />}
                onClick={download}
                sx={{ textTransform: "none", fontSize: 12 }}
              >
                Download
              </Button>
            </Tooltip>
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <ForecastGrid node={product} closed={closed} darkMode={darkMode} />
        <Box sx={{ mt: 1.5 }}>
          {product.skus.map((s) => (
            <SkuCard
              key={s.sku}
              sku={s}
              closed={closed}
              darkMode={darkMode}
              textSec={textSec}
              open={!!openSku[s.sku]}
              onToggle={toggleSku}
            />
          ))}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
});
